import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useMemo,
} from 'react';

import { useOktaAuth } from '@okta/okta-react';
import { useHistory } from 'react-router-dom';

import {
  fetchCandidatesWithReviewStatus,
  fetchCandidateWithStatus,
  getCurrentUser,
} from 'api';
import { settings } from 'config';
import { useSnackContext } from 'hooks/useSnackContext';
import { CandidateApplicationProps, EmployerFilterSummary } from 'types';

export type CandidateContextProps = {
  candidateArray: Array<CandidateApplicationProps>;
  candidates: Record<string, CandidateApplicationProps>;
  candidatesLoaded: boolean;
  getCandidate: (candidate_id: number) => Promise<void>;
  updateCandidate: (updatedData: Partial<CandidateApplicationProps>) => void;
  loadData: () => void;
  setCandidates: React.Dispatch<
    React.SetStateAction<Record<string, CandidateApplicationProps>>
  >;
  filterSummary: EmployerFilterSummary;
  submitted: boolean;
  toggleSubmitted: () => void;
};

export const CandidateContext = createContext<CandidateContextProps>(
  undefined!,
);
export const CandidateProvider = (props: { children: ReactNode }) => {
  const { oktaAuth } = useOktaAuth();
  const history = useHistory();

  const [candidateArray, setCandidateArray] = useState<
    Array<CandidateApplicationProps>
  >([]);
  const [candidates, setCandidates] = useState<
    Record<string, CandidateApplicationProps>
  >({});
  const [candidatesLoaded, setCandidatesLoaded] = useState(false);
  //Rename update and use arrow functuions to remove deps

  const [submitted, setSubmitted] = useState(false);

  const { setSnack } = useSnackContext();

  const toggleSubmitted = useCallback(
    () => setSubmitted((state) => !state),
    [setSubmitted],
  );

  const filterSummary = useMemo(() => {
    return candidateArray.reduce(
      (previousValue, currentValue) => {
        const { review_status } = currentValue;

        previousValue.all += 1;

        if (review_status === 'pending_user_review') {
          previousValue.unchecked += 1;
        }
        if (review_status === 'review_in_progress') {
          previousValue.inProgress += 1;
        }
        if (review_status === 'did_not_work_certain') {
          previousValue.didNotWorkForUs += 1;
        }
        if (review_status === 'did_not_work_insufficient_info') {
          previousValue.insufficientData += 1;
        }
        if (review_status === 'reviewed_and_verified') {
          previousValue.verified += 1;
        }
        return previousValue;
      },
      {
        all: 0,
        unchecked: 0,
        inProgress: 0,
        didNotWorkForUs: 0,
        insufficientData: 0,
        verified: 0,
      },
    );
  }, [candidateArray]);

  const updateCandidate = useCallback(
    async (updatedData: Partial<CandidateApplicationProps>) => {
      const { id } = updatedData;
      setCandidates((prevData) => ({
        ...prevData,
        [id!]: { ...prevData[id!], ...updatedData },
      }));

      setCandidateArray((prevData) => {
        const newCandidateArray = [...prevData].map((candidate) =>
          candidate.id === id
            ? {
                ...candidate,
                ...updatedData,
              }
            : candidate,
        );

        return newCandidateArray;
      });
    },
    [],
  );

  const getCandidate = useCallback(
    async (candidate_id: number) => {
      try {
        const data = await fetchCandidateWithStatus(candidate_id);
        if (data) {
          setCandidates((prevData) => ({
            ...prevData,
            [candidate_id]: data,
          }));

          setCandidateArray((prevData) => {
            const index = prevData.findIndex(({ id }) => id === data.id);
            const newArray = [...prevData];
            if (index === -1) {
              newArray.push(data);
            } else {
              newArray[index] = data;
            }
            return newArray;
          });
        }
      } catch (e) {
        setSnack((props) => ({
          ...props,
          severity: 'error',
          open: true,
          message: 'Failed To Fetch Candidate',
          error: e as Error,
        }));
      }
    },
    [setSnack],
  );

  const loadData = useCallback(async () => {
    try {
      setCandidatesLoaded(false);
      const { data } = await fetchCandidatesWithReviewStatus({
        params: {
          limit: 900000,
          include_children: true,
        },
      });
      const arr = data
        .map((obj) => {
          const { review_status, review_status_comment, candidate } = obj;
          return {
            ...candidate,
            review_status: review_status,
            review_status_comment: review_status_comment,
          };
        })
        //remove duplicate candidates resulting from reviews from multiple orgs in the parent child relationship
        .reduce(
          (
            acc: Array<CandidateApplicationProps>,
            current: CandidateApplicationProps,
          ) => {
            const x = acc.find((candidate) => candidate.id === current.id);
            if (!x) {
              return acc.concat([current]);
            }
            return acc;
          },
          [],
        );

      setCandidateArray(arr);
      setCandidates(Object.fromEntries(arr.map((a) => [a.id, a])));
      setCandidatesLoaded(true);
    } catch (e) {
      setSnack((props) => ({
        ...props,
        severity: 'error',
        open: true,
        message: 'Failed To Fetch Candidates',
        error: e as Error,
      }));
      setCandidateArray([]);
      setCandidates({});
      setCandidatesLoaded(true);
    }
  }, [setSnack]);

  const checkUserStatus = useCallback(async () => {
    const signOut = async () => {
      await oktaAuth.signOut({
        clearTokensBeforeRedirect: true,
        postLogoutRedirectUri: settings.APP_ORIGINAL_URI,
        state: 'logged_out',
      });
    };
    try {
      const { data } = await getCurrentUser();
      if (data.account_status === 'disabled') {
        signOut();
        history.push('/');
      }
    } catch (e) {
      setSnack((props) => ({
        ...props,
        severity: 'error',
        open: true,
        message: 'Failed To Log Out User',
        error: e as Error,
      }));
    }
  }, [setSnack, history, oktaAuth]);

  useEffect(() => {
    loadData();
    const intervalId = setInterval(loadData, 5 * 60 * 1000);
    return () => {
      setCandidateArray([]);
      setCandidates({});
      setCandidatesLoaded(false);
      clearInterval(intervalId);
    };
  }, [loadData]);
  // checks if user is disabled every minute and logs them out if they are.
  useEffect(() => {
    const intervalId = setInterval(checkUserStatus, 60 * 1000);
    return () => {
      clearInterval(intervalId);
    };
  });

  const memoizedReturn = useMemo(
    () => ({
      candidateArray,
      candidatesLoaded,
      candidates,
      loadData,
      updateCandidate,
      setCandidates,
      getCandidate,
      filterSummary,
      submitted,
      toggleSubmitted,
    }),
    [
      candidateArray,
      candidatesLoaded,
      candidates,
      loadData,
      updateCandidate,
      setCandidates,
      getCandidate,
      filterSummary,
      submitted,
      toggleSubmitted,
    ],
  );

  return (
    <CandidateContext.Provider value={memoizedReturn}>
      {props.children}
    </CandidateContext.Provider>
  );
};
